import { createSelector } from '@reduxjs/toolkit';
import { alphaSort } from 'vet-bones/utils';

import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { MappingFilterItem } from 'src/containers/ProjectMapping/types';
import { projectClassRelationshipsSelector } from 'src/selectors/projectClassRelationships';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { ReduxState } from 'src/store/types';
import {
  MappingFilterCheckState,
  MappingFilterType,
} from 'src/store/ui/constants';
import * as copy from 'src/templates/copy';

export const ALL_RELATIONSHIPS_CHECKED_STATE = {
  [MappingFilterType.ALL_ITEMS]: MappingFilterCheckState.CHECKED,
  [MappingFilterType.DIRECT_ITEMS]: MappingFilterCheckState.CHECKED,
  [MappingFilterType.INHERITED_ITEMS]: MappingFilterCheckState.CHECKED,
};

const filterItemSorter = alphaSort<MappingFilterItem>(({ text }) => text);

export const projectMappingRelationshipsFiltersByIriSelector =
  (projectId: string, mappingId: string) => (state: ReduxState) =>
    state.ui.filtersByProjectMapping[projectId]?.[mappingId] || {};

export const projectMappingRelationshipsFilterSetsSelector = (
  projectId: string,
  classIri: string,
  mappingId: string
) =>
  createSelector(
    projectMappingRelationshipsFiltersByIriSelector(projectId, mappingId),
    projectClassRelationshipsSelector(projectId, classIri),
    (filtersByIri = {}, relationshipsByIri) => {
      const relationships = Object.values(relationshipsByIri);
      const allRelationships = new Set<string>();
      const hiddenRelationships = new Set<string>();

      const directRelationships = new Set<string>();
      const hiddenDirectRelationships = new Set<string>();

      const inheritedRelationships = new Set<string>();
      const hiddenInheritedRelationships = new Set<string>();
      if (relationships.length) {
        relationships.forEach(({ iri, depth }) => {
          const isHidden = filtersByIri[iri] === false;
          allRelationships.add(iri);
          if (isHidden) {
            hiddenRelationships.add(iri);
          }

          if (depth === 1) {
            directRelationships.add(iri);
            if (isHidden) {
              hiddenDirectRelationships.add(iri);
            }
          } else {
            inheritedRelationships.add(iri);
            if (isHidden) {
              hiddenInheritedRelationships.add(iri);
            }
          }
        });
      }
      return {
        allRelationships,
        hiddenRelationships,
        directRelationships,
        hiddenDirectRelationships,
        inheritedRelationships,
        hiddenInheritedRelationships,
      };
    }
  );

export const projectMappingRelationshipFiltersSelector = (
  projectId: string,
  classIri: string,
  mappingId: string
) =>
  createSelector(
    projectMappingRelationshipsFilterSetsSelector(
      projectId,
      classIri,
      mappingId
    ),
    ({
      allRelationships,
      hiddenRelationships,
      directRelationships,
      hiddenDirectRelationships,
      inheritedRelationships,
      hiddenInheritedRelationships,
    }) => {
      if (!allRelationships.size || !hiddenRelationships.size) {
        return ALL_RELATIONSHIPS_CHECKED_STATE;
      }

      let allRelationshipsValue = MappingFilterCheckState.CHECKED;
      let directRelationshipsValue = MappingFilterCheckState.CHECKED;
      let inheritedRelationshipsValue = MappingFilterCheckState.CHECKED;
      if (hiddenRelationships.size) {
        allRelationshipsValue =
          allRelationships.size === hiddenRelationships.size
            ? MappingFilterCheckState.UNCHECKED
            : MappingFilterCheckState.INDETERMINATE;
      }
      if (hiddenDirectRelationships.size) {
        directRelationshipsValue =
          directRelationships.size === hiddenDirectRelationships.size
            ? MappingFilterCheckState.UNCHECKED
            : MappingFilterCheckState.INDETERMINATE;
      }
      if (hiddenInheritedRelationships.size) {
        inheritedRelationshipsValue =
          inheritedRelationships.size === hiddenInheritedRelationships.size
            ? MappingFilterCheckState.UNCHECKED
            : MappingFilterCheckState.INDETERMINATE;
      }

      return {
        [MappingFilterType.ALL_ITEMS]: allRelationshipsValue,
        [MappingFilterType.DIRECT_ITEMS]: directRelationshipsValue,
        [MappingFilterType.INHERITED_ITEMS]: inheritedRelationshipsValue,
      };
    }
  );

export const projectMappingRelationshipFilterItemsSelector = (
  projectId: string,
  classIri: string,
  mappingId: string
) =>
  createSelector(
    projectMappingRelationshipsFilterSetsSelector(
      projectId,
      classIri,
      mappingId
    ),
    projectMappingRelationshipFiltersSelector(projectId, classIri, mappingId),
    projectLabelsByIriSelector(projectId),
    (
      { hiddenRelationships, directRelationships, inheritedRelationships },
      filterState,
      labelsByIri
    ) => {
      const directRelationshipSummaryItem: MappingFilterItem = {
        text: copy.components.projectMapping.filter.directRelationships,
        value: MappingFilterType.DIRECT_ITEMS,
        checkState: filterState.DIRECT_ITEMS,
      };
      const directRelationshipItems: MappingFilterItem[] = Array.from(
        directRelationships
      )
        .map((value) => ({
          value,
          text: getLabelForIri(labelsByIri, value),
          checkState: hiddenRelationships.has(value)
            ? MappingFilterCheckState.UNCHECKED
            : MappingFilterCheckState.CHECKED,
        }))
        .sort(filterItemSorter);

      const inheritedRelationshipSummaryItem: MappingFilterItem = {
        text: copy.components.projectMapping.filter.inheritedRelationships,
        value: MappingFilterType.INHERITED_ITEMS,
        checkState: filterState.INHERITED_ITEMS,
      };
      const inheritedRelationshipItems: MappingFilterItem[] = Array.from(
        inheritedRelationships
      )
        .map((value) => ({
          value,
          text: getLabelForIri(labelsByIri, value),
          checkState: hiddenRelationships.has(value)
            ? MappingFilterCheckState.UNCHECKED
            : MappingFilterCheckState.CHECKED,
        }))
        .sort(filterItemSorter);

      const allRelationshipItems = [
        directRelationshipSummaryItem,
        ...directRelationshipItems,
        inheritedRelationshipSummaryItem,
        ...inheritedRelationshipItems,
      ];

      return {
        allRelationshipItems,
        directRelationshipItems,
        inheritedRelationshipItems,
      };
    }
  );
